import { type JSX, useEffect, useState } from "react";

import {
  type InvocationInput,
  type Operation,
  type ParamSpec,
  type ResolvedExample,
  canRun,
  resolveExample,
} from "../api/catalog";
import { BenchApiError } from "../api/http";
import type { Prefill } from "../hooks/useCatalog";
import type { LookupKind } from "../api/lookup";
import { ExamplePicker } from "./ExamplePicker";
import { WebhookSigner } from "./WebhookSigner";
import { LookupField } from "./LookupField";
import { SchemaForm, initialBody } from "./SchemaForm";

interface OperationPanelProps {
  operation: Operation;
  /** Fields answered by a "Check this" button on the previous result, applied on open. */
  prefill: Prefill | null;
  /** The chosen identity, for a deployment that mints its own tokens. */
  profile: string;
  /** Roles carried by the chosen identity's token, read from the token itself. */
  roles: string[];
  destructiveAllowed: boolean;
  /** Whether the bench can find a repository's webhook secret itself. */
  canDiscoverSecrets: boolean;
  projectSet: boolean;
  busy: boolean;
  onRun: (input: InvocationInput) => void;
}

/**
 * The form for one operation: path and query parameters as typed fields, the request body from
 * its JSON Schema, and whatever guidance the overlay supplies.
 */
export function OperationPanel({
  operation,
  prefill,
  profile,
  roles,
  destructiveAllowed,
  canDiscoverSecrets,
  projectSet,
  busy,
  onRun,
}: OperationPanelProps): JSX.Element {
  const [pathParams, setPathParams] = useState<Record<string, string>>({});
  const [query, setQuery] = useState<Record<string, unknown>>({});
  const [body, setBody] = useState<unknown>(undefined);
  const [rawBody, setRawBody] = useState("");
  const [headers, setHeaders] = useState<Record<string, string>>({});
  const [confirmText, setConfirmText] = useState("");

  const [prefilling, setPrefilling] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);

  // Reset every field when the tester moves to a different task, so nothing leaks across --
  // except whatever a verification check carried in, which is the reason for the move.
  //
  // A check names an example as well as carrying values, because the point is that Run is the
  // only click left: the example supplies a complete body (which carry cannot express) and the
  // carried values then aim it at the record the previous call just touched. So the example is
  // applied first and the carry second — order matters.
  useEffect(() => {
    let cancelled = false;
    const carried = {
      pathParams: { ...prefill?.pathParams },
      query: pick(prefill?.query, operation),
    };

    setPathParams(carried.pathParams);
    setQuery({ ...operation.defaults, ...carried.query });
    setBody(initialBody(operation));
    setRawBody(operation.raw_body ? "{}" : "");
    setHeaders({ ...operation.header_defaults });
    setConfirmText("");
    setPrefillError(null);

    if (!prefill?.example) {
      setPrefilling(false);
      return;
    }

    setPrefilling(true);
    void resolveExample(operation.operation_id, prefill.example)
      .then((example) => {
        if (cancelled) {
          return;
        }
        setPathParams({ ...asStrings(example.path_params), ...carried.pathParams });
        setQuery({ ...operation.defaults, ...example.query, ...carried.query });
        if (operation.raw_body) {
          setRawBody(example.body === null ? "{}" : JSON.stringify(example.body, null, 2));
        } else if (example.body !== null) {
          setBody(example.body);
        }
        setHeaders({ ...operation.header_defaults, ...example.extra_headers });
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          // The carried values are already in place, so the form is still usable — just not
          // one-click. Saying so beats leaving the tester to guess why the body is empty.
          setPrefillError(
            `Could not load the "${prefill.example}" example: ${describeCause(cause)}`,
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPrefilling(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [operation, prefill]);

  const pathFields = operation.params.filter((p) => p.location === "path");
  const queryFields = operation.params.filter((p) => p.location === "query");

  const missingPath = pathFields.filter((p) => !(pathParams[p.name] ?? "").trim());
  const missingQuery = queryFields.filter((p) => p.required && isEmpty(query[p.name]));
  const blockedByProject = operation.project_required && !projectSet;
  const blockedByDanger = operation.danger !== "none" && !destructiveAllowed;
  const needsConfirm = operation.danger === "severe";
  const confirmed = !needsConfirm || confirmText === operation.path;

  const loadExample = (example: ResolvedExample): void => {
    setPathParams(asStrings(example.path_params));
    setQuery({ ...example.query });
    if (operation.raw_body) {
      setRawBody(example.body === null ? "{}" : JSON.stringify(example.body, null, 2));
    } else if (example.body !== null) {
      setBody(example.body);
    }
    setHeaders({ ...example.extra_headers });
    setConfirmText("");
  };

  const rawBodyError = operation.raw_body ? jsonError(rawBody) : null;
  // Signing has to cover the bytes that will actually be sent, so it works from the parsed body.
  const parsedRawBody = rawBodyError === null && operation.raw_body ? safeParse(rawBody) : null;
  const signatureHeader = operation.extra_header_fields.find((name) =>
    /signature/i.test(name),
  );

  // Whether the service is expected to refuse this identity. A warning, never a block: the bench
  // exists to check that AMS enforces its own rules, and a Run button the bench disables proves
  // nothing. So this says what should happen, and then lets you go and see whether it does.
  const expectRefusal = !canRun(operation, roles);
  const confinedBySystem = roles.includes("system") && !operation.system_allowed;

  const blocked =
    busy ||
    // Running mid-prefill would send a half-filled body.
    prefilling ||
    blockedByDanger ||
    blockedByProject ||
    missingPath.length > 0 ||
    missingQuery.length > 0 ||
    rawBodyError !== null ||
    !confirmed;

  return (
    <section className="panel">
      <header className="panel__header">
        <div>
          <h2>{operation.task_label}</h2>
          <p className="muted">
            <code>
              {operation.method} {operation.path}
            </code>
          </p>
        </div>
        <div className="badge-row">
          {operation.required_profile && (
            <span
              className="flag flag--on"
              title="The identity this call is meant for. Others are expected to be refused."
            >
              wants {operation.required_profile}
            </span>
          )}
          {operation.returns_job && <span className="flag flag--off">polled for you</span>}
          {operation.danger !== "none" && (
            <span className={`flag flag--${operation.danger === "severe" ? "danger" : "warn"}`}>
              {operation.danger}
            </span>
          )}
        </div>
      </header>

      {(operation.help_text || operation.summary) && (
        <p>{operation.help_text ?? operation.summary}</p>
      )}

      {expectRefusal && (
        // Red, and before the form rather than after the result: the tester should know what the
        // service is going to say before they press Run, so that a 403 reads as the expected
        // answer instead of a bench that is broken.
        <div className="notice notice--error">
          {confinedBySystem ? (
            <>
              <strong>Expect 403 Forbidden.</strong> <code>{profile}</code> carries the{" "}
              <code>system</code> role, and the Memory Service refuses a system token on every
              path outside <code>/v1/lifecycle</code> &mdash; that role is a confinement, not a
              grant. Run it to see the refusal, or switch identity on Set up to make it work.
            </>
          ) : (
            <>
              <strong>Expect 403 Forbidden.</strong> This call requires{" "}
              <code>{operation.required_roles.join(" and ")}</code>, and <code>{profile}</code>{" "}
              carries <code>{roles.length ? roles.join(", ") : "no roles"}</code>. Run it to see
              the refusal, or switch identity on Set up to make it work.
            </>
          )}
        </div>
      )}

      {blockedByDanger && (
        <div className="notice notice--warning">
          <strong>Disabled on this deployment.</strong> This operation changes or deletes data,
          and the environment is not configured to allow that.
        </div>
      )}

      {blockedByProject && (
        <div className="notice notice--warning">
          <strong>This operation is project-scoped.</strong> Set a project in the scope panel
          first — the Memory Service rejects the call otherwise.
        </div>
      )}

      {prefilling && (
        <p className="muted">Filling this in from the &ldquo;{prefill?.example}&rdquo; example…</p>
      )}

      {prefillError && (
        <div className="notice notice--warning">
          <strong>Only partly filled in.</strong> {prefillError} The values carried over from the
          previous call are in place; pick an example below to complete the rest.
        </div>
      )}

      {!prefilling && !prefillError && prefill?.example && (
        <p className="muted">
          Filled in from the &ldquo;{prefill.example}&rdquo; example, aimed at what the previous
          call touched. Press <strong>Run</strong>.
        </p>
      )}

      <ExamplePicker operation={operation} onLoad={loadExample} />

      {pathFields.length > 0 && (
        <div className="form-grid form-grid--tight">
          {pathFields.map((param) => (
            <ParamField
              key={param.name}
              param={param}
              lookup={lookupFor(operation, param.name)}
              value={pathParams[param.name] ?? ""}
              onChange={(value) => setPathParams({ ...pathParams, [param.name]: value })}
            />
          ))}
        </div>
      )}

      {queryFields.length > 0 && (
        <details className="details" open={queryFields.some((p) => p.required)}>
          <summary>Filters and options ({queryFields.length})</summary>
          <div className="form-grid form-grid--tight">
            {queryFields.map((param) => (
              <QueryField
                key={param.name}
                param={param}
                lookup={lookupFor(operation, param.name)}
                value={query[param.name]}
                onChange={(value) => setQuery({ ...query, [param.name]: value })}
              />
            ))}
          </div>
        </details>
      )}

      {operation.body_schema && (
        <>
          <SchemaForm operation={operation} value={body} onChange={setBody} />
        </>
      )}

      {operation.raw_body && (
        <label className="raw-body">
          <span>Request body (raw JSON)</span>
          <textarea
            className="json-editor"
            rows={10}
            value={rawBody}
            onChange={(event) => setRawBody(event.target.value)}
          />
          <small className="muted">
            This payload&rsquo;s shape is the provider&rsquo;s own and is not described by the
            service&rsquo;s schema, so it is entered by hand.
          </small>
          {rawBodyError && <small className="raw-body__error">{rawBodyError}</small>}
        </label>
      )}

      {signatureHeader && (
        <WebhookSigner
          provider={String(pathParams["provider"] ?? "gitea")}
          headerName={signatureHeader}
          repositoryId={String(pathParams["repository_id"] ?? "")}
          body={parsedRawBody}
          canDiscover={canDiscoverSecrets}
          onSigned={(name, value) => setHeaders((current) => ({ ...current, [name]: value }))}
        />
      )}

      {operation.extra_header_fields.length > 0 && (
        <div className="form-grid form-grid--tight">
          {operation.extra_header_fields.map((name) => (
            <label key={name}>
              <span>{name}</span>
              <input
                value={headers[name] ?? ""}
                onChange={(event) => setHeaders({ ...headers, [name]: event.target.value })}
              />
              <small className="muted">
                Sent as a request header. This route is verified with the repository&rsquo;s HMAC
                secret rather than a token.
              </small>
            </label>
          ))}
        </div>
      )}

      {needsConfirm && destructiveAllowed && (
        <div className="notice notice--error">
          <strong>This permanently deletes data.</strong> Type <code>{operation.path}</code> to
          confirm.
          <input
            className="confirm"
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
            aria-label="Confirmation"
          />
        </div>
      )}

      <div className="panel__actions">
        <button
          type="button"
          className="primary"
          disabled={blocked}
          onClick={() =>
            onRun({
              operation_id: operation.operation_id,
              path_params: pathParams,
              query,
              ...(operation.body_schema ? { body: body ?? {} } : {}),
              ...(operation.raw_body ? { body: JSON.parse(rawBody) } : {}),
              ...(Object.keys(headers).length > 0 ? { extra_headers: headers } : {}),
            })
          }
        >
          {busy ? "Running…" : "Run"}
        </button>
        {missingPath.length > 0 && (
          <span className="muted">
            Needs {missingPath.map((p) => p.name).join(", ")}
          </span>
        )}
        {missingQuery.length > 0 && (
          <span className="muted">
            Needs {missingQuery.map((p) => p.name).join(", ")}
          </span>
        )}
      </div>
    </section>
  );
}

function ParamField({
  param,
  lookup,
  value,
  onChange,
}: {
  param: ParamSpec;
  lookup: LookupKind | null;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  if (lookup) {
    return (
      <LookupField kind={lookup} value={value} onChange={onChange} label={param.name} required />
    );
  }
  return (
    <label>
      <span>
        {param.name} <em>required</em>
      </span>
      <input value={value} onChange={(event) => onChange(event.target.value)} />
      {param.description && <small className="muted">{param.description}</small>}
    </label>
  );
}

function QueryField({
  param,
  lookup,
  value,
  onChange,
}: {
  param: ParamSpec;
  lookup: LookupKind | null;
  value: unknown;
  onChange: (value: unknown) => void;
}): JSX.Element {
  const options = enumOf(param);

  if (lookup) {
    return (
      <LookupField
        kind={lookup}
        value={typeof value === "string" ? value : ""}
        onChange={onChange}
        label={param.name}
        required={param.required}
      />
    );
  }

  return (
    <label>
      <span>
        {param.name} {param.required && <em>required</em>}
      </span>

      {options ? (
        param.explode ? (
          <select
            multiple
            value={Array.isArray(value) ? (value as string[]) : []}
            onChange={(event) =>
              onChange([...event.target.selectedOptions].map((option) => option.value))
            }
          >
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : (
          <select
            value={typeof value === "string" ? value : ""}
            onChange={(event) => onChange(event.target.value)}
          >
            <option value="">(any)</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )
      ) : typeOf(param) === "boolean" ? (
        <select
          value={value === true ? "true" : value === false ? "false" : ""}
          onChange={(event) =>
            onChange(event.target.value === "" ? undefined : event.target.value === "true")
          }
        >
          <option value="">(unset)</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : (
        <input
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      )}

      {param.description && <small className="muted">{param.description}</small>}
    </label>
  );
}

/** Pull an enum out of a parameter schema, including through a nullable `anyOf`. */
function enumOf(param: ParamSpec): string[] | null {
  const direct = param.schema_["enum"];
  if (Array.isArray(direct)) {
    return direct.map(String);
  }
  const items = param.schema_["items"];
  if (items && typeof items === "object") {
    const nested = (items as Record<string, unknown>)["enum"];
    if (Array.isArray(nested)) {
      return nested.map(String);
    }
  }
  for (const branch of branchesOf(param.schema_)) {
    if (Array.isArray(branch["enum"])) {
      return branch["enum"].map(String);
    }
  }
  return null;
}

function typeOf(param: ParamSpec): string | null {
  const direct = param.schema_["type"];
  if (typeof direct === "string") {
    return direct;
  }
  // AMS models optional values as anyOf: [{type: X}, {type: "null"}].
  for (const branch of branchesOf(param.schema_)) {
    if (typeof branch["type"] === "string" && branch["type"] !== "null") {
      return branch["type"];
    }
  }
  return null;
}

function branchesOf(schema: Record<string, unknown>): Array<Record<string, unknown>> {
  const anyOf = schema["anyOf"] ?? schema["oneOf"];
  if (!Array.isArray(anyOf)) {
    return [];
  }
  return anyOf.filter(
    (entry): entry is Record<string, unknown> => typeof entry === "object" && entry !== null,
  );
}

function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null || value === "") {
    return true;
  }
  return Array.isArray(value) && value.length === 0;
}

/** Whether the overlay asks for a picker on this parameter. */
function lookupFor(operation: Operation, field: string): LookupKind | null {
  const hint = operation.ui_hints[field] as { "ui:lookup"?: LookupKind } | undefined;
  return hint?.["ui:lookup"] ?? null;
}

/** Validate the raw-body editor as the tester types, so Run is only offered valid JSON. */
function jsonError(text: string): string | null {
  if (text.trim() === "") {
    return "A body is required for this operation.";
  }
  try {
    JSON.parse(text);
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : "Not valid JSON.";
  }
}

/** Path parameters are always rendered as text inputs, whatever the example's YAML type. */
function asStrings(values: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, value === null ? "" : String(value)]),
  );
}

/** Parse the raw-body editor's contents, or null when it is not valid JSON yet. */
function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Keep only the carried values this operation actually accepts as query parameters.
 *
 * A check carries a value without knowing whether the target takes it in the path or the query,
 * so it offers both. Filtering here stops the path copy from also being sent as a stray filter.
 */
function pick(
  carried: Record<string, unknown> | undefined,
  operation: Operation,
): Record<string, unknown> {
  if (!carried) {
    return {};
  }
  const queryNames = new Set(
    operation.params.filter((param) => param.location === "query").map((param) => param.name),
  );
  return Object.fromEntries(
    Object.entries(carried).filter(([name]) => queryNames.has(name)),
  );
}

function describeCause(cause: unknown): string {
  return cause instanceof BenchApiError ? cause.message : String(cause);
}
